import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  BulkCreateTemplateItemsMutation,
  CreateTemplateItemMutation,
  DeleteTemplateItemMutation,
  TemplateItemsQuery,
  TemplateQuery,
  TermTemplateQuery,
  UpdateTemplateItemMutation,
} from "../graphql/templates.graphql";

export interface Template {
  templateId: string;
  termId: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface TemplateItem {
  templateItemId: string;
  templateId: string;
  category: string;
  timeEstimate: string | null;
  itemName: string;
  question: string | null;
  note: string | null;
  isRequired: boolean;
  order: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateTemplateItemInput {
  templateId: string;
  category: string;
  itemName: string;
  question?: string | null;
  timeEstimate?: string | null;
  note?: string | null;
  isRequired?: boolean;
  order?: number;
}

export interface UpdateTemplateItemInput {
  templateItemId: string;
  category?: string | null;
  itemName?: string | null;
  question?: string | null;
  timeEstimate?: string | null;
  note?: string | null;
  isRequired?: boolean | null;
  order?: number | null;
}

export function useTemplate(templateId: string | null) {
  const [result, reexecute] = useQuery({
    query: TemplateQuery,
    variables: { templateId: templateId ?? "" },
    pause: !templateId,
  });

  return {
    template: (result.data?.template as Template | undefined) ?? null,
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useTermTemplate(termId: string | null) {
  const [result, reexecute] = useQuery({
    query: TermTemplateQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  return {
    template: (result.data?.termTemplate as Template | undefined) ?? null,
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useTemplateItems(templateId: string | null) {
  const [result, reexecute] = useQuery({
    query: TemplateItemsQuery,
    variables: { templateId: templateId ?? "" },
    pause: !templateId,
  });

  return {
    templateItems: (result.data?.templateItems.nodes ?? []) as TemplateItem[],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export interface BulkTemplateItemInput {
  category: string;
  itemName: string;
  question?: string | null;
  timeEstimate?: string | null;
  note?: string | null;
  isRequired?: boolean;
  order?: number;
}

export function useTemplateMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [, executeCreate] = useMutation(CreateTemplateItemMutation);
  const [, executeUpdate] = useMutation(UpdateTemplateItemMutation);
  const [, executeDelete] = useMutation(DeleteTemplateItemMutation);
  const [, executeBulkCreate] = useMutation(BulkCreateTemplateItemsMutation);

  const createTemplateItem = useCallback(
    async (data: CreateTemplateItemInput): Promise<TemplateItem | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeCreate({ input: data });
        if (result.error) throw new Error(result.error.message);
        return (result.data?.createTemplateItem as TemplateItem) ?? null;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "テンプレートアイテムの作成に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeCreate],
  );

  const updateTemplateItem = useCallback(
    async (data: UpdateTemplateItemInput): Promise<TemplateItem | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeUpdate({ input: data });
        if (result.error) throw new Error(result.error.message);
        return (
          (result.data?.updateTemplateItem as TemplateItem | undefined) ?? null
        );
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "テンプレートアイテムの更新に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeUpdate],
  );

  const deleteTemplateItem = useCallback(
    async (templateItemId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeDelete({ templateItemId });
        if (result.error) throw new Error(result.error.message);
        return result.data?.deleteTemplateItem ?? false;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "テンプレートアイテムの削除に失敗しました";
        setError(message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeDelete],
  );

  const bulkCreateTemplateItems = useCallback(
    async (
      templateId: string,
      items: BulkTemplateItemInput[],
    ): Promise<TemplateItem[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeBulkCreate({
          input: { templateId, items },
        });
        if (result.error) throw new Error(result.error.message);
        return (
          (result.data?.bulkCreateTemplateItems.nodes as TemplateItem[]) ?? null
        );
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "テンプレートアイテムの一括作成に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeBulkCreate],
  );

  return {
    createTemplateItem,
    updateTemplateItem,
    deleteTemplateItem,
    bulkCreateTemplateItems,
    loading,
    error,
  };
}
